// getDisplayMedia/getUserMedia + MediaRecorder, wrapped as a hook. Capture concerns only —
// RecorderPage owns the upload queue and wires push()ed chunks into it; this file never
// touches the network, matching the FR-RD7/FR-P2 invariant that recording cannot depend on it.
//
// Screen + camera, and camera-only with an effect, are composited through a <canvas>
// (compositor.ts) rather than recorded from the raw track, which is what makes FR-RD3's
// draggable bubble and camera filters adjustable *during* a recording: the settings object
// mutates, the captured track never changes. See compositor.ts's module doc for why that
// indirection is load-bearing. Camera-only with no effect skips it and records the camera
// track directly (see requestSource) — so its effect can only be chosen before recording.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  CameraCompositor,
  OVERLAY_CAPTURE_FPS,
  waitForVideoReady,
  type BlurState,
  type CompositorSettings,
} from "./compositor";
import { defaultBubble, type CameraBubble, type CameraFilterId } from "./cameraFx";
import { captureBitrateBps } from "./ladder";
import { recDiag } from "./diagnostics";
import {
  base64ToBlob,
  float32ToS16leBase64,
  isDesktop,
  isMobileShell,
  listNativeSources,
  listenDesktopPayload,
  loadDesktopInfo,
  nativeAudioPush,
  nativeCapturePause,
  nativeCaptureStart,
  nativeCaptureStop,
  nativePreviewJpeg,
  peekDesktopInfo,
  requestMobilePermissions,
  type NativeCaptureSource,
} from "../desktop";
import { explainCaptureError } from "./permissions";

export type SourceMode = "screen" | "camera" | "screen-camera";
export type RecorderPhase = "idle" | "priming" | "ready" | "recording" | "paused" | "stopped" | "error";

export interface DeviceOption {
  deviceId: string;
  label: string;
}

/** The capture's tracks and settled pixel dimensions, handed out once at the moment recording
 * starts. Since SD25 nothing in the browser consumes this to *process* the recording (FR-P3c
 * — the server does all of that); it is what the capture actually is, which the recorder UI
 * and any future capture-side telemetry (§11 Q16) still want to know. */
export interface CaptureInfo {
  videoTrack: MediaStreamTrack;
  audioTrack: MediaStreamTrack | null;
  width: number;
  height: number;
}

const LEVEL_BAR_COUNT = 7;

/** FR-RW5 (SD23, still load-bearing under SD25 for two server-side reasons): H.264 first,
 * because an H.264 master is what the server can *packet-copy* into HLS inside FR-P12's budget
 * — no encode anywhere — and an H.264 MP4 master is what plays progressively in every
 * browser while a deferred grackle waits for that copy (FR-PL1b). A VP9/VP8 capture can only be
 * published by an encode, which since SD25 never happens automatically at all. Order: H.264-in-MP4 (Safari 14.5+, Chromium
 * 126+), H.264-in-WebM (Chromium), then the legacy VP9/VP8 WebM fallback (Firefox has no
 * H.264 MediaRecorder at all — §11 Q16).
 *
 * Two SD24 lessons baked in:
 * - Every H.264 candidate carries an explicit profile-level string. Chrome rejects the generic
 *   `codecs=avc1` spelling, and level matters: 640033 (High 5.1) covers up to 4K capture,
 *   42E01E is the maximally-compatible constrained-baseline fallback.
 * - NEVER a bare `video/mp4` candidate: a bare container says nothing about the codec, and
 *   Chrome answers it with VP9-in-MP4 — a source that looks like the fast path and remuxes
 *   like the slow one, observed in production as a 3398×1194 recording that fell all the way
 *   to a server re-encode.
 *
 * `isSupported` is injectable so this is testable without a MediaRecorder global (Node has
 * none). */
export function pickMimeType(
  isSupported: (candidate: string) => boolean = (candidate) =>
    typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(candidate),
): string {
  const candidates = [
    "video/mp4;codecs=avc1.640033,mp4a.40.2",
    "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
    "video/webm;codecs=h264,opus",
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
  ];
  for (const candidate of candidates) {
    if (isSupported(candidate)) return candidate;
  }
  return "";
}

/** Turns one analyser time-domain snapshot into `barCount` bar heights in [0, 1]: RMS of
 * each equal segment of the buffer, with a fixed gain so ordinary speech reads as mostly-full
 * bars without clipping on a raised voice. Pure — takes data, returns numbers. */
export function computeLevels(timeDomainData: Uint8Array, barCount: number): number[] {
  if (barCount <= 0) return [];
  const segmentSize = Math.max(1, Math.floor(timeDomainData.length / barCount));
  const levels: number[] = [];
  for (let i = 0; i < barCount; i++) {
    const start = i * segmentSize;
    const end = i === barCount - 1 ? timeDomainData.length : Math.min(start + segmentSize, timeDomainData.length);
    let sumSquares = 0;
    let count = 0;
    for (let j = start; j < end; j++) {
      const centered = (timeDomainData[j] - 128) / 128;
      sumSquares += centered * centered;
      count += 1;
    }
    const rms = count > 0 ? Math.sqrt(sumSquares / count) : 0;
    levels.push(Math.min(1, rms * 3.5));
  }
  return levels;
}

function stopTracks(stream: MediaStream | null | undefined): void {
  stream?.getTracks().forEach((t) => t.stop());
}

async function listDevices(): Promise<{ microphones: DeviceOption[]; cameras: DeviceOption[] }> {
  const all = await navigator.mediaDevices.enumerateDevices();
  const toOption = (d: MediaDeviceInfo, fallback: string): DeviceOption => ({
    deviceId: d.deviceId,
    label: d.label || fallback,
  });
  const microphones = all
    .filter((d) => d.kind === "audioinput")
    .map((d, i) => toOption(d, `Microphone ${i + 1}`));
  const cameras = all.filter((d) => d.kind === "videoinput").map((d, i) => toOption(d, `Camera ${i + 1}`));
  return { microphones, cameras };
}

async function jpegPreviewStream(b64: string): Promise<MediaStream> {
  const img = new Image();
  img.src = `data:image/jpeg;base64,${b64}`;
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("native preview failed"));
  });
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth || 1280;
  canvas.height = img.naturalHeight || 720;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("native preview canvas unavailable");
  ctx.drawImage(img, 0, 0);
  return canvas.captureStream(1);
}

function startMicPcmPush(micStream: MediaStream, audioCtx: AudioContext): () => void {
  const src = audioCtx.createMediaStreamSource(micStream);
  const proc = audioCtx.createScriptProcessor(4096, 1, 1);
  proc.onaudioprocess = (ev) => {
    const f32 = ev.inputBuffer.getChannelData(0);
    void nativeAudioPush(float32ToS16leBase64(f32)).catch(() => {});
  };
  const mute = audioCtx.createGain();
  mute.gain.value = 0;
  src.connect(proc);
  proc.connect(mute);
  mute.connect(audioCtx.destination);
  return () => {
    proc.disconnect();
    src.disconnect();
    mute.disconnect();
  };
}

async function hiddenVideoFor(stream: MediaStream): Promise<HTMLVideoElement> {
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.srcObject = stream;
  await video.play().catch(() => {});
  await waitForVideoReady(video);
  return video;
}

export interface UseRecorderResult {
  phase: RecorderPhase;
  error: string | null;
  mode: SourceMode | null;
  previewStream: MediaStream | null;
  micLevels: number[];
  microphones: DeviceOption[];
  cameras: DeviceOption[];
  microphoneId: string | null;
  cameraId: string | null;
  elapsedMs: number;
  mimeType: string;
  /** Where the camera bubble sits, in canvas-relative units (FR-RD3). `null` until a
   * composited camera source exists. */
  cameraBubble: CameraBubble | null;
  /** The composited canvas's pixel size — what the drag layer needs to undo the preview's
   * `object-fit: cover` crop and put the handle exactly over the bubble. */
  overlaySize: { width: number; height: number } | null;
  cameraFilter: CameraFilterId;
  blurState: BlurState;
  /** Camera-only recording that started without an effect records the camera track directly,
   * so an effect can't be switched on until it stops. */
  cameraEffectsLocked: boolean;
  /** Whether the current screen/screen-camera share actually came with a system-audio track
   * (FR-RW3 disclosure) — `null` when the mode has no screen share to report on, or while one
   * is still being requested. `getDisplayMedia`'s `audio: true` is best-effort and browsers
   * give no separate signal for "declined" vs "unsupported", so this is read back from the
   * resulting stream rather than predicted up front. */
  systemAudioCaptured: boolean | null;
  /** True when screen capture is the native ffmpeg path, not getDisplayMedia. */
  usingNative: boolean;
  nativeSources: NativeCaptureSource[];
  nativeSourceId: string | null;
  setNativeSourceId: (id: string) => void;
  /** Requests permission and wires up preview + level meter for `mode`. Safe to call again
   * (e.g. on device change) — it tears down whatever was active first. */
  requestSource: (
    mode: SourceMode,
    deviceOverride?: { microphoneId?: string; cameraId?: string; nativeSourceId?: string },
  ) => Promise<void>;
  setMicrophoneId: (id: string) => void;
  setCameraId: (id: string) => void;
  /** Both of these apply on the compositor's next frame and are safe mid-recording — that is
   * the point of compositing (compositor.ts). Neither touches the captured track. */
  setCameraBubble: (bubble: CameraBubble) => void;
  setCameraFilter: (filter: CameraFilterId) => void;
  /** Returns the live video/audio tracks and their pixel dimensions — `null` if there is no
   * active stream to record (phase wasn't "ready"). Reading `combinedStreamRef`'s tracks here
   * rather than exposing the ref itself keeps useRecorder the single owner of stream
   * lifetime. */
  startRecording: () => CaptureInfo | null;
  /** FR-RD4: pause/resume the in-flight MediaRecorder without tearing down capture. */
  pauseRecording: () => void;
  resumeRecording: () => void;
  /** Resolves once the final chunk has been delivered to onChunk (MediaRecorder always fires
   * one last "dataavailable" before "stop"), so the caller can safely flush its tail part
   * immediately after awaiting this. */
  stopRecording: () => Promise<void>;
  /** Releases every device/track and resets to "idle" without producing more chunks. */
  reset: () => void;
}

export function useRecorder(onChunk: (chunk: Blob) => void): UseRecorderResult {
  const [phase, setPhase] = useState<RecorderPhase>("idle");
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<SourceMode | null>(null);
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);
  const [micLevels, setMicLevels] = useState<number[]>(() => new Array(LEVEL_BAR_COUNT).fill(0));
  const [microphones, setMicrophones] = useState<DeviceOption[]>([]);
  const [cameras, setCameras] = useState<DeviceOption[]>([]);
  const [microphoneId, setMicrophoneIdState] = useState<string | null>(null);
  const [cameraId, setCameraIdState] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [cameraBubble, setCameraBubbleState] = useState<CameraBubble | null>(null);
  const [overlaySize, setOverlaySize] = useState<{ width: number; height: number } | null>(null);
  const [cameraFilter, setCameraFilterState] = useState<CameraFilterId>("none");
  const [blurState, setBlurState] = useState<BlurState>("off");
  const [compositing, setCompositing] = useState(false);
  const [systemAudioCaptured, setSystemAudioCaptured] = useState<boolean | null>(null);
  const [usingNative, setUsingNative] = useState(false);
  const [nativeSources, setNativeSources] = useState<NativeCaptureSource[]>([]);
  const [nativeSourceId, setNativeSourceIdState] = useState<string | null>(null);
  const [captureMime, setCaptureMime] = useState(() => pickMimeType());

  const onChunkRef = useRef(onChunk);
  onChunkRef.current = onChunk;

  const screenStreamRef = useRef<MediaStream | null>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const combinedStreamRef = useRef<MediaStream | null>(null);
  // Compositing (see compositor.ts): hidden <video> elements decode the raw screen/camera
  // tracks so a <canvas> can draw them; overlayStreamRef holds the canvas's own
  // captureStream() so its track can be stopped on cleanup like any other.
  const overlayScreenVideoRef = useRef<HTMLVideoElement | null>(null);
  const overlayCameraVideoRef = useRef<HTMLVideoElement | null>(null);
  const overlayStreamRef = useRef<MediaStream | null>(null);
  const compositorRef = useRef<CameraCompositor | null>(null);
  // Bubble placement and filter choice are *user* settings, so they survive the teardown that
  // a device change or a mode switch does — refs, not just state, because requestSource reads
  // them while building the next compositor and must not capture a stale render's copy.
  const settingsRef = useRef<CompositorSettings>({ bubble: { x: 0, y: 0, radiusFraction: 0 }, filter: "none" });
  const bubbleChosenRef = useRef(false);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const levelFrameRef = useRef<number | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const mimeType = captureMime;
  const usingNativeRef = useRef(false);
  const nativeUnsubRef = useRef<Array<() => void>>([]);
  const pcmStopRef = useRef<(() => void) | null>(null);
  const nativeEndedWaitRef = useRef<(() => void) | null>(null);

  const stopLevelMeter = useCallback(() => {
    if (levelFrameRef.current !== null) {
      cancelAnimationFrame(levelFrameRef.current);
      levelFrameRef.current = null;
    }
    setMicLevels(new Array(LEVEL_BAR_COUNT).fill(0));
  }, []);

  const teardownCapture = useCallback(() => {
    recDiag.detachAll();
    compositorRef.current?.stop();
    compositorRef.current = null;
    setCompositing(false);
    stopTracks(overlayStreamRef.current);
    stopTracks(screenStreamRef.current);
    stopTracks(cameraStreamRef.current);
    stopTracks(micStreamRef.current);
    if (overlayScreenVideoRef.current) overlayScreenVideoRef.current.srcObject = null;
    if (overlayCameraVideoRef.current) overlayCameraVideoRef.current.srcObject = null;
    overlayScreenVideoRef.current = null;
    overlayCameraVideoRef.current = null;
    overlayStreamRef.current = null;
    screenStreamRef.current = null;
    cameraStreamRef.current = null;
    micStreamRef.current = null;
  }, []);

  const releaseAll = useCallback(() => {
    stopLevelMeter();
    teardownCapture();
    combinedStreamRef.current = null;
    if (audioCtxRef.current) {
      void audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    setPreviewStream(null);
    setOverlaySize(null);
    setSystemAudioCaptured(null);
    setUsingNative(false);
    usingNativeRef.current = false;
    for (const u of nativeUnsubRef.current) u();
    nativeUnsubRef.current = [];
    pcmStopRef.current?.();
    pcmStopRef.current = null;
  }, [stopLevelMeter, teardownCapture]);

  const startLevelMeter = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const data = new Uint8Array(analyser.fftSize);
    const tick = () => {
      analyser.getByteTimeDomainData(data);
      setMicLevels(computeLevels(data, LEVEL_BAR_COUNT));
      levelFrameRef.current = requestAnimationFrame(tick);
    };
    levelFrameRef.current = requestAnimationFrame(tick);
  }, []);

  /** Builds the canvas pipeline for any mode with a camera in it and returns the track
   * MediaRecorder should see. `screenStream` is null in camera-only mode. */
  const startCompositor = useCallback(
    async (screenStream: MediaStream | null, cameraStream: MediaStream): Promise<MediaStreamTrack> => {
      const cameraVideo = await hiddenVideoFor(cameraStream);
      const screenVideo = screenStream ? await hiddenVideoFor(screenStream) : null;
      const canvas = document.createElement("canvas");
      const sizeSource = screenVideo ?? cameraVideo;
      canvas.width = sizeSource.videoWidth || 1280;
      canvas.height = sizeSource.videoHeight || 720;

      const aspect = canvas.width / canvas.height;
      // First composited source of the session decides the resting position; after that the
      // user's own placement wins, re-clamped in case the new canvas has a different shape.
      const bubble = bubbleChosenRef.current
        ? settingsRef.current.bubble
        : defaultBubble(aspect);
      settingsRef.current = { ...settingsRef.current, bubble };
      setCameraBubbleState(bubble);
      setOverlaySize({ width: canvas.width, height: canvas.height });

      overlayScreenVideoRef.current = screenVideo;
      overlayCameraVideoRef.current = cameraVideo;

      const compositor = new CameraCompositor({
        canvas,
        screenVideo,
        cameraVideo,
        settings: settingsRef.current,
        onBlurState: setBlurState,
        onDraw: recDiag.draw,
      });
      compositorRef.current = compositor;
      setCompositing(true);
      compositor.start();

      const canvasStream = canvas.captureStream(OVERLAY_CAPTURE_FPS);
      overlayStreamRef.current = canvasStream;
      recDiag.attachCameraVideo(cameraVideo);
      recDiag.attachTrack("canvas", canvasStream.getVideoTracks()[0]);
      return canvasStream.getVideoTracks()[0];
    },
    [],
  );

  const requestSource = useCallback(
    async (
      nextMode: SourceMode,
      deviceOverride?: { microphoneId?: string; cameraId?: string; nativeSourceId?: string },
    ) => {
      setError(null);
      setPhase("priming");
      releaseAll();

      const micId = deviceOverride?.microphoneId ?? microphoneId ?? undefined;
      const camId = deviceOverride?.cameraId ?? cameraId ?? undefined;

      try {
        const caps = isDesktop()
          ? (await loadDesktopInfo())?.capabilities ?? peekDesktopInfo()?.capabilities
          : null;

        // SD32: Android/iOS need the OS grant before WebView getUserMedia will
        // prompt. Kotlin/Swift own the Activity/UIApplication request.
        if (isMobileShell()) {
          await requestMobilePermissions(["microphone", "camera", "notifications"]).catch(
            () => undefined,
          );
        }

        // Tag a mic failure so the error names the microphone, not the selected mode's
        // video source (FR-RD9: tell the user which grant is actually missing).
        const micStream = await navigator.mediaDevices
          .getUserMedia({
            audio: micId ? { deviceId: { exact: micId } } : true,
          })
          .catch((err: unknown) => {
            throw Object.assign(err instanceof Error ? err : new Error(String(err)), {
              grackleSource: "microphone" as const,
            });
          });
        micStreamRef.current = micStream;
        recDiag.attachTrack("mic", micStream.getAudioTracks()[0]);

        let videoTrack: MediaStreamTrack;
        const extraAudioStreams: MediaStream[] = [];

        // Native screen (FR-RD1 display/window): ffmpeg grab + H.264. Camera overlay
        // still needs the compositor, so screen-camera stays on getDisplayMedia.
        if (
          nextMode === "screen" &&
          isDesktop() &&
          caps?.native_capture_ready
        ) {
          try {
            const sources = await listNativeSources();
            setNativeSources(sources);
            const wanted = deviceOverride?.nativeSourceId ?? nativeSourceId;
            // F15a: a `region:x,y,w,h` id is not enumerated; it is cropped natively.
            const chosen =
              (wanted && (wanted.startsWith("region:") ? wanted : sources.find((s) => s.id === wanted)?.id)) ||
              sources.find((s) => s.kind === "display")?.id ||
              sources[0]?.id;
            if (!chosen) throw new Error("No native display or window is available.");
            setNativeSourceIdState(chosen);
            const jpeg = await nativePreviewJpeg(chosen).catch((err) => {
              const display = chosen.startsWith("region:") ? sources.find((s) => s.kind === "display")?.id : null;
              if (!display) throw err;
              return nativePreviewJpeg(display);
            });
            const preview = await jpegPreviewStream(jpeg);
            videoTrack = preview.getVideoTracks()[0];
            overlayStreamRef.current = preview;
            usingNativeRef.current = true;
            setUsingNative(true);
            setSystemAudioCaptured(Boolean(caps.audio.system_loopback_ready));
            setCaptureMime("video/mp4");
          } catch (nativeErr) {
            // Native failed — keep the webview picker so capture still works.
            usingNativeRef.current = false;
            setUsingNative(false);
            setNativeSources([]);
            const screenStream = await navigator.mediaDevices.getDisplayMedia({
              video: true,
              audio: true,
            });
            screenStreamRef.current = screenStream;
            videoTrack = screenStream.getVideoTracks()[0];
            const hasSystemAudio = screenStream.getAudioTracks().length > 0;
            setSystemAudioCaptured(hasSystemAudio);
            if (hasSystemAudio) extraAudioStreams.push(screenStream);
            videoTrack.addEventListener("ended", () => {
              if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
                mediaRecorderRef.current.stop();
              }
            });
            // Fall through to getDisplayMedia without treating this as a broken recorder.
            void nativeErr;
          }
        } else if (nextMode === "screen" || nextMode === "screen-camera") {
          const screenStream = await navigator.mediaDevices.getDisplayMedia({
            video: true,
            audio: true, // best-effort tab/system audio; many browsers simply omit the track
          });
          screenStreamRef.current = screenStream;
          const screenTrack = screenStream.getVideoTracks()[0];
          recDiag.attachTrack("screen", screenTrack);
          const hasSystemAudio = screenStream.getAudioTracks().length > 0;
          setSystemAudioCaptured(hasSystemAudio);
          if (hasSystemAudio) extraAudioStreams.push(screenStream);
          // If the user stops sharing from the browser's own "Stop sharing" bar, treat it
          // exactly like clicking Stop in our UI. Bound to the real screen track even in
          // screen-camera mode — the canvas's own captureStream() track never "ends" on its
          // own, since nothing tells a canvas its source disappeared.
          screenTrack.addEventListener("ended", () => {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
              mediaRecorderRef.current.stop();
            }
          });

          if (nextMode === "screen") {
            videoTrack = screenTrack;
          } else {
            const cameraStream = await navigator.mediaDevices.getUserMedia({
              video: camId ? { deviceId: { exact: camId } } : true,
            });
            cameraStreamRef.current = cameraStream;
            recDiag.attachTrack("camera", cameraStream.getVideoTracks()[0]);
            videoTrack = await startCompositor(screenStream, cameraStream);
          }
        } else {
          const cameraStream = await navigator.mediaDevices.getUserMedia({
            video: camId ? { deviceId: { exact: camId } } : true,
          });
          cameraStreamRef.current = cameraStream;
          recDiag.attachTrack("camera", cameraStream.getVideoTracks()[0]);
          // Camera-only with no effect has nothing to composite, so MediaRecorder gets the
          // camera track itself. Going through the canvas costs a GPU draw plus a readback of
          // every frame before encoding; on some Linux GPU stacks that readback is slow enough
          // that the encoder falls behind real time (frames queue inside MediaRecorder and are
          // lost if the renderer dies; observed 2026-10-26 as 26 s of chunks holding 13.6 s).
          videoTrack =
            settingsRef.current.filter === "none"
              ? cameraStream.getVideoTracks()[0]
              : await startCompositor(null, cameraStream);
        }

        const audioCtx = new AudioContext();
        audioCtxRef.current = audioCtx;
        const destination = audioCtx.createMediaStreamDestination();
        const micSource = audioCtx.createMediaStreamSource(micStream);
        micSource.connect(destination);
        for (const extra of extraAudioStreams) {
          audioCtx.createMediaStreamSource(extra).connect(destination);
        }

        // The level meter reads the microphone alone (design/Meters.dc.html: "Microphone
        // level"), never the mixed/system audio, and is never connected to audioCtx's
        // destination — connecting it to actual speakers would create an audible loop.
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        micSource.connect(analyser);
        analyserRef.current = analyser;

        const combined = new MediaStream([videoTrack, ...destination.stream.getAudioTracks()]);
        combinedStreamRef.current = combined;
        setPreviewStream(combined);

        const { microphones: mics, cameras: cams } = await listDevices();
        setMicrophones(mics);
        setCameras(cams);
        if (!micId && mics.length > 0) setMicrophoneIdState(micStream.getAudioTracks()[0]?.getSettings().deviceId ?? mics[0].deviceId);
        else if (micId) setMicrophoneIdState(micId);
        if (nextMode === "camera" || nextMode === "screen-camera") {
          if (!camId && cams.length > 0) {
            setCameraIdState(cameraStreamRef.current?.getVideoTracks()[0]?.getSettings().deviceId ?? cams[0].deviceId);
          } else if (camId) {
            setCameraIdState(camId);
          }
        }

        setMode(nextMode);
        startLevelMeter();
        setPhase("ready");
      } catch (err) {
        releaseAll();
        setPhase("error");
        const caps = peekDesktopInfo()?.capabilities;
        const failedSource =
          err && typeof err === "object" && "grackleSource" in err ? "microphone" : null;
        const kind =
          failedSource ??
          (nextMode === "camera" ? "camera" : nextMode === "screen" ? "screen" : "mixed");
        setError(explainCaptureError(err, caps, kind));
      }
    },
    [microphoneId, cameraId, nativeSourceId, releaseAll, startLevelMeter, startCompositor],
  );

  const setMicrophoneId = useCallback(
    (id: string) => {
      setMicrophoneIdState(id);
      if (phase === "ready" && mode) void requestSource(mode, { microphoneId: id });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [phase, mode],
  );

  const setCameraId = useCallback(
    (id: string) => {
      setCameraIdState(id);
      // Re-requesting re-prompts getDisplayMedia's screen/window picker in screen-camera mode
      // (the browser has no "swap just this track" API) — an accepted rough edge for a first
      // version of the overlay, matching the same full-re-request pattern camera-only mode
      // already uses for a device change. Note this is *not* how the bubble position or a
      // filter change is applied: those mutate the running compositor instead, precisely so
      // they stay usable mid-recording.
      if (phase === "ready" && (mode === "camera" || mode === "screen-camera")) {
        void requestSource(mode, { cameraId: id });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [phase, mode],
  );

  const setCameraBubble = useCallback((bubble: CameraBubble) => {
    bubbleChosenRef.current = true;
    settingsRef.current = { ...settingsRef.current, bubble };
    setCameraBubbleState(bubble);
    compositorRef.current?.update({ bubble });
  }, []);

  const setNativeSourceId = useCallback(
    (id: string) => {
      setNativeSourceIdState(id);
      if (phase === "ready" && mode === "screen") {
        void requestSource(mode, { nativeSourceId: id });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [phase, mode],
  );

  const setCameraFilter = useCallback(
    (filter: CameraFilterId) => {
      settingsRef.current = { ...settingsRef.current, filter };
      setCameraFilterState(filter);
      // Camera-only switches between the direct camera track (no effect) and the compositor
      // (an effect) by re-requesting the camera, which has no picker to re-prompt. Only before
      // recording: mid-recording the captured track can't change (`cameraEffectsLocked`).
      if (mode === "camera" && phase === "ready" && (filter === "none") === Boolean(compositorRef.current)) {
        void requestSource("camera");
        return;
      }
      compositorRef.current?.update({ filter });
      if (!compositorRef.current) setBlurState("off");
    },
    [mode, phase, requestSource],
  );

  const startRecording = useCallback((): CaptureInfo | null => {
    const stream = combinedStreamRef.current;
    if (!stream || phase !== "ready") return null;
    const videoTrack = stream.getVideoTracks()[0];
    const settings = videoTrack?.getSettings();

    if (usingNativeRef.current) {
      const sourceId = nativeSourceId;
      const caps = peekDesktopInfo()?.capabilities;
      const includeLoopback = Boolean(caps?.audio.system_loopback_ready);
      void (async () => {
        try {
          const unsubs: Array<() => void> = [];
          unsubs.push(
            await listenDesktopPayload<{ data_b64: string; mime_type: string }>(
              "grackle://capture/chunk",
              (payload) => {
                if (!payload?.data_b64) return;
                onChunkRef.current(base64ToBlob(payload.data_b64, payload.mime_type || "video/mp4"));
              },
            ),
          );
          unsubs.push(
            await listenDesktopPayload<{ message: string; permission_id?: string | null }>(
              "grackle://capture/error",
              (payload) => {
                setError(
                  explainCaptureError(
                    payload?.message ?? "Native capture failed.",
                    caps,
                    "screen",
                  ),
                );
              },
            ),
          );
          unsubs.push(
            await listenDesktopPayload("grackle://capture/ended", () => {
              nativeEndedWaitRef.current?.();
              nativeEndedWaitRef.current = null;
            }),
          );
          nativeUnsubRef.current = unsubs;
          const mime = await nativeCaptureStart({
            sourceId: sourceId ?? "display:0",
            includeSystemAudio: includeLoopback,
            includeMic: true,
          });
          if (mime) setCaptureMime(mime);
          if (micStreamRef.current && audioCtxRef.current) {
            pcmStopRef.current = startMicPcmPush(micStreamRef.current, audioCtxRef.current);
          }
        } catch (err) {
          setError(explainCaptureError(err, caps, "screen"));
          setPhase("error");
        }
      })();
      setPhase("recording");
      setElapsedMs(0);
      elapsedTimerRef.current = setInterval(() => setElapsedMs((ms) => ms + 250), 250);
      return {
        videoTrack,
        audioTrack: stream.getAudioTracks()[0] ?? null,
        width: settings?.width ?? 1280,
        height: settings?.height ?? 720,
      };
    }

    // SD23/FR-RW5: the captured bitstream is what viewers get (the remux copies it verbatim),
    // so the browser's bitrate choice is bounded here rather than left to its default — which
    // for screen capture is routinely several times what the content needs.
    const options: MediaRecorderOptions = {
      ...(mimeType ? { mimeType } : {}),
      ...(settings?.height ? { videoBitsPerSecond: captureBitrateBps(settings.height) } : {}),
    };
    const recorder = new MediaRecorder(stream, options);
    recorder.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) onChunkRef.current(ev.data);
    };
    recorder.onerror = () => {
      setError("Recording stopped unexpectedly.");
    };
    mediaRecorderRef.current = recorder;
    recorder.start(1000); // 1s timeslice (build-plan-m1.md §1 WP11)
    recDiag.start(recorder);
    setPhase("recording");
    setElapsedMs(0);
    elapsedTimerRef.current = setInterval(() => setElapsedMs((ms) => ms + 250), 250);

    if (!videoTrack || !settings?.width || !settings?.height) return null;
    return {
      videoTrack,
      audioTrack: stream.getAudioTracks()[0] ?? null,
      width: settings.width,
      height: settings.height,
    };
  }, [phase, mimeType, nativeSourceId]);

  const pauseRecording = useCallback(() => {
    if (usingNativeRef.current) {
      void nativeCapturePause(true).catch(() => {});
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current);
        elapsedTimerRef.current = null;
      }
      setPhase("paused");
      return;
    }
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== "recording") return;
    recorder.pause();
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
    setPhase("paused");
  }, []);

  const resumeRecording = useCallback(() => {
    if (usingNativeRef.current) {
      void nativeCapturePause(false).catch(() => {});
      elapsedTimerRef.current = setInterval(() => setElapsedMs((ms) => ms + 250), 250);
      setPhase("recording");
      return;
    }
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== "paused") return;
    recorder.resume();
    elapsedTimerRef.current = setInterval(() => setElapsedMs((ms) => ms + 250), 250);
    setPhase("recording");
  }, []);

  const stopRecording = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current);
        elapsedTimerRef.current = null;
      }
      if (usingNativeRef.current) {
        pcmStopRef.current?.();
        pcmStopRef.current = null;
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          nativeEndedWaitRef.current = null;
          for (const u of nativeUnsubRef.current) u();
          nativeUnsubRef.current = [];
          stopLevelMeter();
          teardownCapture();
          setPreviewStream(null);
          setPhase("stopped");
          resolve();
        };
        nativeEndedWaitRef.current = finish;
        void nativeCaptureStop().finally(() => {
          window.setTimeout(finish, 400);
        });
        return;
      }
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === "inactive") {
        setPhase("stopped");
        resolve();
        return;
      }
      recorder.addEventListener(
        "stop",
        () => {
          recDiag.stop();
          stopLevelMeter();
          teardownCapture();
          setPreviewStream(null);
          setPhase("stopped");
          resolve();
        },
        { once: true },
      );
      recorder.stop();
    });
  }, [stopLevelMeter, teardownCapture]);

  const reset = useCallback(() => {
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
    recDiag.stop();
    mediaRecorderRef.current = null;
    releaseAll();
    setPhase("idle");
    setMode(null);
    setError(null);
    setElapsedMs(0);
  }, [releaseAll]);

  useEffect(() => releaseAll, [releaseAll]);

  return {
    phase,
    error,
    mode,
    previewStream,
    micLevels,
    microphones,
    cameras,
    microphoneId,
    cameraId,
    elapsedMs,
    mimeType,
    cameraBubble,
    overlaySize,
    cameraFilter,
    blurState,
    cameraEffectsLocked: mode === "camera" && (phase === "recording" || phase === "paused") && !compositing,
    systemAudioCaptured,
    usingNative,
    nativeSources,
    nativeSourceId,
    setNativeSourceId,
    requestSource,
    setMicrophoneId,
    setCameraId,
    setCameraBubble,
    setCameraFilter,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    reset,
  };
}
